import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageSection } from "@/components/AppLayout.jsx";
import { GsdReadableBlock, ModuleCard } from "@/coquille-bridge";
import { DataTable } from "@/components/DataTable.jsx";
import { getDataStore } from "@/data/dataStore.js";
import { useAuth } from "@/context/AuthContext.jsx";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import { useCurrentMembership, useMembershipCopy } from "@/membership/useMembership.js";
import { filterEventsForViewer, nextUpcoming } from "@/membership/membershipCalendar.js";
import { canViewHealthRecord } from "@/membership/healthPermissions.js";
import { canViewBlackDragonContent, isBlackDragonActive } from "@/membership/blackDragonEngine.js";
import { appendHealthAudit, submitBdRequest } from "@/membership/membershipStore.js";
import { formatFee } from "@/membership/membershipI18n.js";
import { BLACK_DRAGON_STATUSES, MEMBERSHIP_STATUSES } from "@/membership/membershipConstants.js";
import { ExamsListPanel, StudentProgressView, useProgressScope } from "@/components/progress/KarateProgressPanels.jsx";
import { ADULT_HEALTH_FIELDS, MINOR_HEALTH_FIELDS } from "@/membership/healthFormCatalog.js";

function requireAuth(locale, testId, title) {
  return (
    <PageSection title={title} testId={testId}>
      <p>
        <Link to="/connexion">{locale === "en" ? "Sign in" : "Connexion"}</Link>
      </p>
    </PageSection>
  );
}

export function MemberTodayExtras() {
  const { locale, copy } = useMembershipCopy();
  const { membership, isActive, isBlackDragon } = useCurrentMembership();
  const { localizeDemo } = useKarateUi();
  const store = getDataStore();
  const events = filterEventsForViewer(store.events, { role: "student", dojoId: membership?.identity?.dojoId }, membership);
  const nextEvent = nextUpcoming(events);
  const classes = (store.classes || []).filter((c) => c.dojo_id === membership?.identity?.dojoId && c.statut === "actif");
  const nextClass = classes[0];
  if (!membership) return null;
  return (
    <div className="home-grid" data-testid="membership-today">
      <ModuleCard title={copy.portal.todayTitle} testId="membership-today-card">
        <p data-testid="membership-today-status">
          {copy.statuses[membership.status]} {isActive ? <span className="membership-badge membership-badge--active">{copy.wizard.activated}</span> : null}
        </p>
        <p data-testid="membership-today-class">
          {copy.portal.nextClass} : {nextClass ? localizeDemo(nextClass.nom) : copy.portal.noClass}
        </p>
        <p data-testid="membership-today-event">
          {copy.portal.nextEvent} : {nextEvent ? localizeDemo(nextEvent.titre) : copy.portal.noEvent}
        </p>
        <p>
          {copy.portal.reminders} : {membership.periodEnd}
        </p>
        <ul className="link-list">
          <li>
            <Link to="/mon-calendrier">{copy.portal.calendarTitle}</Link>
          </li>
          <li>
            <Link to="/mon-adhesion">{copy.portal.adhesionTitle}</Link>
          </li>
          <li>
            <Link to="/espace-black-dragon">{copy.portal.bdTitle}</Link>
          </li>
        </ul>
        {isBlackDragon ? <p data-testid="membership-today-bd">{copy.portal.bdActive}</p> : null}
      </ModuleCard>
    </div>
  );
}

export function MemberCalendarPage() {
  const { isAuthenticated } = useAuth();
  const { locale, copy } = useMembershipCopy();
  const { membership } = useCurrentMembership();
  const { localizeDemo } = useKarateUi();
  const store = getDataStore();
  if (!isAuthenticated) return requireAuth(locale, "page-mon-calendrier", copy.portal.calendarTitle);
  const rows = filterEventsForViewer(store.events, { role: "student", dojoId: membership?.identity?.dojoId }, membership).map((e) => ({
    ...e,
    titre: localizeDemo(e.titre),
    place: localizeDemo(store.dojos.find((d) => d.id === e.dojo_id)?.nom || "—"),
  }));
  return (
    <PageSection title={copy.portal.calendarTitle} subtitle={copy.portal.calendarSubtitle} testId="page-mon-calendrier">
      <DataTable
        rows={rows}
        columns={[
          { key: "date", label: copy.publicCalendar.columns.date, sortable: true },
          { key: "titre", label: copy.publicCalendar.columns.title, sortable: true },
          { key: "place", label: copy.publicCalendar.columns.place, sortable: true },
        ]}
        searchFields={["titre", "place", "date"]}
        emptyMessage={copy.portal.emptyCalendar}
        emptyFilteredMessage={copy.portal.emptyCalendar}
        testId="member-calendar-table"
      />
    </PageSection>
  );
}

export function MemberAdhesionPage() {
  const { isAuthenticated, username, role } = useAuth();
  const { locale, copy } = useMembershipCopy();
  const { membership, viewer, store } = useCurrentMembership();
  if (!isAuthenticated) return requireAuth(locale, "page-mon-adhesion", copy.portal.adhesionTitle);
  const canHealth = canViewHealthRecord(viewer, membership, store.settings);
  const fields = membership?.kind === "minor" ? MINOR_HEALTH_FIELDS : ADULT_HEALTH_FIELDS;
  useEffect(() => {
    if (canHealth && membership?.id) {
      appendHealthAudit({ actorUsername: username, actorRole: role, membershipId: membership.id, action: "view_self" });
    }
  }, [canHealth, membership?.id, username, role]);
  return (
    <PageSection title={copy.portal.adhesionTitle} subtitle={copy.portal.adhesionSubtitle} testId="page-mon-adhesion">
      {!membership ? (
        <p>
          <Link to="/devenir-membre">{copy.homeCta}</Link>
        </p>
      ) : (
        <GsdReadableBlock testId="member-adhesion-panel">
          <p data-testid="member-adhesion-status">
            {copy.statuses[membership.status]} — {membership.identity.firstName} {membership.identity.lastName}
          </p>
          <p>
            {copy.portal.startOn} : {membership.periodStart || "—"} · {copy.portal.renewOn} : {membership.periodEnd || "—"}
          </p>
          <p>
            {copy.portal.amount} : {formatFee(membership.payment?.amountCents, membership.payment?.currency, locale)}
          </p>
          <p data-testid="member-adhesion-payment">
            {copy.portal.transaction} : {membership.payment?.transactionId || "—"} ({membership.payment?.provider || "sandbox"})
          </p>
          <h3>{copy.portal.healthSelf}</h3>
          {canHealth ? (
            <dl data-testid="member-health-self">
              {fields.filter((f) => f.sensitive !== false).slice(0, 8).map((f) => (
                <React.Fragment key={f.id}>
                  <dt>{copy.health[f.id]}</dt>
                  <dd>{String(membership.health?.answers?.[f.id] ?? "—")}</dd>
                </React.Fragment>
              ))}
            </dl>
          ) : (
            <p className="membership-restricted" data-testid="member-health-hidden">
              {copy.portal.healthHidden}
            </p>
          )}
        </GsdReadableBlock>
      )}
    </PageSection>
  );
}

export function MemberExamsPage() {
  const { isAuthenticated } = useAuth();
  const { locale, copy } = useMembershipCopy();
  const scope = useProgressScope();
  if (!isAuthenticated) return requireAuth(locale, "page-mes-examens", copy.portal.examsTitle);
  return (
    <PageSection title={copy.portal.examsTitle} subtitle={copy.portal.examsSubtitle} testId="page-mes-examens">
      <StudentProgressView testId="member-exams-progress" />
      <ExamsListPanel scope={scope} testId="member-exams-list" />
    </PageSection>
  );
}

export function MemberBlackDragonPage() {
  const { isAuthenticated, username, role } = useAuth();
  const { locale, copy } = useMembershipCopy();
  const { localizeDemo } = useKarateUi();
  const { membership, isActive, isBlackDragon, store } = useCurrentMembership();
  const [msg, setMsg] = useState("");
  if (!isAuthenticated) return requireAuth(locale, "page-espace-black-dragon", copy.portal.bdTitle);
  const viewer = {
    role,
    isAuthenticated: true,
    isActiveMember: isActive,
    isBlackDragon,
  };
  const visible = store.blackDragonContent.filter((item) => canViewBlackDragonContent(item, viewer));
  const hiddenPrivate = store.blackDragonContent.filter((item) => item.visibility === "black_dragon" && !canViewBlackDragonContent(item, viewer));

  function requestJoin() {
    if (!membership) return;
    const result = submitBdRequest(membership.id, { username, role });
    setMsg(result.ok ? copy.portal.bdPending : copy.portal.bdNeedMember);
  }

  const bdStatus = membership?.blackDragon?.status || BLACK_DRAGON_STATUSES.NOT_MEMBER;

  return (
    <PageSection title={copy.portal.bdTitle} subtitle={copy.portal.bdInviteHint} testId="page-espace-black-dragon">
      {!isActive ? <p data-testid="bd-need-member">{copy.portal.bdNeedMember}</p> : null}
      <p data-testid="bd-member-status">{copy.bdStatuses[bdStatus]}</p>
      {isActive && bdStatus === BLACK_DRAGON_STATUSES.NOT_MEMBER ? (
        <button type="button" className="btn-primary" data-testid="bd-request-btn" onClick={requestJoin}>
          {copy.portal.bdRequest}
        </button>
      ) : null}
      {bdStatus === BLACK_DRAGON_STATUSES.REQUEST_SUBMITTED || bdStatus === BLACK_DRAGON_STATUSES.INVITED ? (
        <p data-testid="bd-pending">{copy.portal.bdPending}</p>
      ) : null}
      {isBlackDragon ? <p data-testid="bd-active-flag">{copy.portal.bdActive}</p> : null}
      {msg ? <p data-testid="bd-request-msg">{msg}</p> : null}
      {visible.map((item) => (
        <article key={item.id} className="membership-content-card" data-testid={`bd-visible-${item.id}`}>
          <h3>{localizeDemo(locale === "en" ? item.titleEn : item.titleFr)}</h3>
          <p>{localizeDemo(locale === "en" ? item.bodyEn : item.bodyFr)}</p>
        </article>
      ))}
      {hiddenPrivate.length ? (
        <p className="membership-restricted" data-testid="bd-private-hidden">
          {copy.portal.bdHidden}
        </p>
      ) : (
        <p data-testid="bd-private-visible">{copy.portal.bdPrivate}</p>
      )}
    </PageSection>
  );
}

export function ParentAdhesionPage() {
  const { isAuthenticated } = useAuth();
  const { locale, copy } = useMembershipCopy();
  const { linked, viewer, store } = useCurrentMembership();
  const [selectedId, setSelectedId] = useState(linked[0]?.id || "");
  const selected = linked.find((m) => m.id === selectedId) || linked[0];
  if (!isAuthenticated) return requireAuth(locale, "page-parent-adhesion", copy.parent.title);
  const canHealth = selected ? canViewHealthRecord(viewer, selected, store.settings) : false;
  return (
    <PageSection title={copy.parent.title} subtitle={copy.parent.subtitle} testId="page-parent-adhesion">
      {linked.length === 0 ? (
        <p data-testid="parent-no-child">{copy.parent.noChild}</p>
      ) : (
        <>
          <label>
            {copy.parent.selectChild}
            <select data-testid="parent-child-switcher" value={selected?.id || ""} onChange={(e) => setSelectedId(e.target.value)}>
              {linked.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.identity.firstName} {m.identity.lastName}
                </option>
              ))}
            </select>
          </label>
          {selected ? (
            <GsdReadableBlock testId="parent-selected-child">
              <p data-testid="parent-child-status">
                {selected.identity.firstName} — {copy.statuses[selected.status]}
              </p>
              <p>
                {copy.portal.renewOn} : {selected.periodEnd}
              </p>
              <Link to="/parent/progression">{copy.portal.examsTitle}</Link>
              {" · "}
              <Link to="/parent/evenements">{copy.portal.calendarTitle}</Link>
              {canHealth ? (
                <p data-testid="parent-child-health">{copy.portal.healthSelf}</p>
              ) : (
                <p data-testid="parent-child-health-hidden">{copy.portal.healthHidden}</p>
              )}
            </GsdReadableBlock>
          ) : null}
        </>
      )}
    </PageSection>
  );
}

export { MEMBERSHIP_STATUSES };
